import { z } from 'zod';
import type { Modality } from '../contracts';
import { customStrategy, customStrategySchema, describeRule, validateCustomDef, type CustomState, type CustomStrategyDef } from './custom';
import type { Strategy } from './types';

/**
 * Ready-made robots of the platform, picked next to the Automação Invexa. Since 2026-10-26 the admin
 * keeps them (Admin › Estratégias, ApplicationSetting `strategies.presets`): name, game, the "Meus robôs"
 * definition, the gales shown, which ones the Automação Invexa runs, and the menu order. Each one runs
 * through the same interpreter as "Meus robôs", so a session reads exactly like the backtest.
 *
 * A session copies the definition when it starts (its params), so editing a robot never changes one
 * already running. Sessions started before that carry `{}` and run the built-in definition of their key.
 */

/** Games the ready-made robots play (Rush is off). */
export const PRESET_MODALITIES = ['EVEN_ODD', 'RISE_FALL'] as const;
export type PresetModality = (typeof PRESET_MODALITIES)[number];

/** perGame: strategies on one game's list, all run together by the Automação Invexa. */
export const PRESET_LIMITS = { name: 60, trigger: 200, gales: 10, total: 40, perGame: 10 } as const;

/** Keys of the platform's own robots that are not ready-made ones. */
const RESERVED_KEYS: readonly string[] = ['invexa_auto', 'invexa_accu'];
/** At most 39 characters: the Automação Invexa copies the key as a robot id (40 at most). */
const PRESET_KEY_RE = /^invexa_[a-z0-9_]{2,32}$/;

/** A ready-made robot's strategy key (`invexa_…`, never the Automação Invexa or the Rush robot). */
export function isInvexaPresetKey(key: string): boolean {
  return PRESET_KEY_RE.test(key) && !RESERVED_KEYS.includes(key);
}

/** Key of a robot created in the admin: never reused, never renamed. */
export function newPresetKey(): string {
  return `invexa_p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export const invexaPresetSchema = z.object({
  key: z.string().refine(isInvexaPresetKey, 'Identificador inválido'),
  name: z.string().trim().min(2, 'Dê um nome (pelo menos 2 letras).').max(PRESET_LIMITS.name),
  /** The trigger as the user reads it; empty = written from the rules (presetSummary). */
  trigger: z.string().trim().max(PRESET_LIMITS.trigger).default(''),
  modality: z.enum(PRESET_MODALITIES),
  /** Gales shown next to the name ("7 gales"): what it was tested with. The user picks their own. */
  gales: z.number().int().min(0).max(PRESET_LIMITS.gales),
  def: customStrategySchema,
  /** Before 2026-10-26 the admin picked the Automação Invexa's robots; now it runs every one on the list. Kept, unused. */
  auto: z.boolean().default(false),
  /** Removed in the admin: off the list, the menu and the Automação Invexa (hidden, never deleted: sessions and history point at it). */
  retired: z.boolean().default(false),
});
export type InvexaPreset = z.infer<typeof invexaPresetSchema>;

/** Errors of a whole list that a single robot can't show. Null = fine. */
export function validatePresetList(list: readonly InvexaPreset[]): string | null {
  if (list.length > PRESET_LIMITS.total) return `No máximo ${PRESET_LIMITS.total} estratégias.`;
  const keys = new Set<string>();
  for (const p of list) {
    if (keys.has(p.key)) return `Identificador repetido: ${p.key}.`;
    keys.add(p.key);
    const error = validateCustomDef(p.def, p.modality);
    if (error) return `${p.name}: ${error}`;
  }
  for (const m of PRESET_MODALITIES) {
    const label = m === 'EVEN_ODD' ? 'Dígitos' : 'Sobe e Desce';
    const names = new Set<string>();
    for (const p of list.filter((x) => x.modality === m && !x.retired)) {
      const n = p.name.trim().toLocaleLowerCase('pt-BR');
      if (names.has(n)) return `${label}: já existe uma estratégia chamada "${p.name}".`;
      names.add(n);
    }
    // The Automação Invexa runs the game's whole list: never empty, never more than it can read each round.
    if (!names.size) return `${label}: a lista precisa de pelo menos 1 estratégia (a Automação Invexa roda as estratégias da lista).`;
    if (names.size > PRESET_LIMITS.perGame) return `${label}: no máximo ${PRESET_LIMITS.perGame} estratégias na lista.`;
  }
  return null;
}

export const invexaPresetListSchema = z.array(invexaPresetSchema).superRefine((list, ctx) => {
  const error = validatePresetList(list);
  if (error) ctx.addIssue({ code: 'custom', message: error });
});

const def = (d: unknown) => customStrategySchema.parse(d);

/**
 * The robots as they were before the admin (owner's decisions 2026-10-24/25): the most cycles with 0
 * lost cycles at 7 gales on all of the previous broker's rounds we held. Used until the admin saves a list,
 * and for sessions started before 2026-10-26 (their params are `{}`). Past results never promise future ones.
 */
export const DEFAULT_INVEXA_PRESETS: readonly InvexaPreset[] = [
  {
    key: 'invexa_par_impar_par',
    name: 'Par, Ímpar, Par',
    trigger: 'Par, Ímpar, Par → entra Par, depois de 1 loss virtual (gale mantém Par)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['EVEN', 'ODD', 'EVEN'], entry: 'EVEN' }], virtualLoss: { count: 1 }, gale: { direction: 'SAME' } }),
    auto: true,
    retired: false,
  },
  {
    key: 'invexa_8de10',
    name: '8 de 10',
    trigger: 'Um lado saiu 8+ vezes nas últimas 10 → entra nesse lado, depois de 1 loss virtual (gale troca de lado)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'FREQUENCY', window: 10, min: 8, entry: 'MAJORITY' }], virtualLoss: { count: 1 }, gale: { direction: 'INVERT' } }),
    auto: true,
    retired: false,
  },
  {
    key: 'invexa_segue6',
    name: 'Segue a sequência',
    trigger: '6 ou mais iguais → entra a favor da sequência',
    modality: 'RISE_FALL',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'STREAK', length: 6, exact: false, entry: 'WITH' }], gale: { direction: 'SIGNAL' } }),
    auto: true,
    retired: false,
  },
  {
    key: 'invexa_sdds',
    name: 'Sobe, Desce, Desce, Sobe',
    trigger: 'Sobe, Desce, Desce, Sobe → entra Desce, depois de 1 loss virtual (gale mantém Desce)',
    modality: 'RISE_FALL',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['RISE', 'FALL', 'FALL', 'RISE'], entry: 'FALL' }], virtualLoss: { count: 1 }, gale: { direction: 'SAME' } }),
    auto: true,
    retired: false,
  },
  {
    // Second on Dígitos until 26/10, when 8 de 10 passed it (alone and paired with Par, Ímpar, Par).
    key: 'invexa_impar4',
    name: '4 Ímpar → Par',
    trigger: '4 Ímpar seguidos → entra Par (gale mantém Par)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['ODD', 'ODD', 'ODD', 'ODD'], entry: 'EVEN' }], gale: { direction: 'SAME' } }),
    auto: false,
    retired: true,
  },
  {
    // Took a lost cycle at 7 gales on 25/10 (owner: keep only the best of the history).
    key: 'invexa_quebra3',
    name: 'Quebra de 3',
    trigger: '3 ou mais iguais e virou → entra no lado da virada (gale troca de lado)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'BREAK', minRun: 3, entry: 'FOLLOW' }], virtualLoss: { count: 1 }, gale: { direction: 'INVERT' } }),
    auto: false,
    retired: true,
  },
];

/** The robots of one game on the menu, in the admin's order. */
export function activePresets(list: readonly InvexaPreset[], modality: Modality): InvexaPreset[] {
  return list.filter((p) => p.modality === modality && !p.retired);
}

/**
 * The robots the Automação Invexa runs on one game: every strategy on its list (owner, 2026-10-26), in list
 * order — when two trigger on the same round, the one above takes the cycle.
 */
export function autoPresets(list: readonly InvexaPreset[], modality: Modality): InvexaPreset[] {
  return activePresets(list, modality).slice(0, PRESET_LIMITS.perGame);
}

/** "sem gale" / "1 gale" / "7 gales". */
export function galesLabel(n: number): string {
  return n <= 0 ? 'sem gale' : n === 1 ? '1 gale' : `${n} gales`;
}

const GALE_TEXT: Record<CustomStrategyDef['gale']['direction'], string> = { SAME: 'gale mantém o lado', INVERT: 'gale troca de lado', SIGNAL: 'gale segue o sinal' };

/** The trigger as the user reads it: the admin's text, or one written from the rules. */
export function presetSummary(p: Pick<InvexaPreset, 'trigger' | 'modality' | 'def'>): string {
  if (p.trigger.trim()) return p.trigger.trim();
  const rules = p.def.rules
    .filter((r) => r.enabled)
    .map((r) => describeRule(r, p.modality))
    .join(' ou ');
  const vl = p.def.virtualLoss.count;
  return `${rules}${vl ? `, depois de ${vl} ${vl === 1 ? 'loss virtual' : 'losses virtuais'}` : ''} (${GALE_TEXT[p.def.gale.direction]})`;
}

// ---------------------------------------------------------------------------

/** What a session of a ready-made robot runs: a copy taken when it starts. `{}` = sessions before 2026-10-26. */
export const presetParamsSchema = z.union([
  z.object({ v: z.literal(1).default(1), name: z.string().min(1).max(PRESET_LIMITS.name), modality: z.enum(PRESET_MODALITIES), def: customStrategySchema }).strict(),
  z.object({}).strict(),
]);
export type PresetParams = z.infer<typeof presetParamsSchema>;

export function presetParams(p: InvexaPreset): PresetParams {
  return { v: 1, name: p.name, modality: p.modality, def: p.def };
}

/** The definition a session runs: its copy, or the built-in one of its key. */
function presetDefOf(key: string, params: unknown): CustomStrategyDef | null {
  const own = (params as { def?: CustomStrategyDef } | null)?.def;
  return own ?? DEFAULT_INVEXA_PRESETS.find((p) => p.key === key)?.def ?? null;
}

function defOrThrow(key: string, params: unknown): CustomStrategyDef {
  const d = presetDefOf(key, params);
  if (!d) throw new Error(`Unknown preset: ${key}`);
  return d;
}

function presetStrategy(key: string): Strategy<PresetParams, CustomState> {
  const builtIn = DEFAULT_INVEXA_PRESETS.find((p) => p.key === key);
  return {
    key,
    name: builtIn?.name ?? 'Estratégia Invexa',
    description: builtIn ? presetSummary(builtIn) : 'Estratégia pronta da Invexa.',
    modalities: builtIn ? [builtIn.modality] : PRESET_MODALITIES,
    paramsSchema: presetParamsSchema as unknown as z.ZodType<PresetParams>,
    defaultParams: () => ({}),
    validate: (params, modality) => {
      const copy = params as { modality?: Modality; def?: CustomStrategyDef };
      if (copy.def) {
        if (copy.modality !== modality) return 'Estratégia não disponível para esta modalidade.';
        return validateCustomDef(copy.def, modality);
      }
      return builtIn ? null : 'Estratégia indisponível.';
    },
    initialState: (params, modality) => customStrategy.initialState(defOrThrow(key, params), modality),
    onTick: (ctx, tick) => customStrategy.onTick({ ...ctx, params: defOrThrow(key, ctx.params) }, tick),
    onSettlement: (ctx, settled) => customStrategy.onSettlement({ ...ctx, params: defOrThrow(key, ctx.params) }, settled),
    shouldEnd: (state, params) => customStrategy.shouldEnd?.(state, defOrThrow(key, params)) ?? null,
    statusNote: customStrategy.statusNote,
  };
}

/** The strategy of a ready-made robot's key (cheap to build: the runner keeps one per session). Null when the key isn't one. */
export function presetStrategyFor(key: string): Strategy<PresetParams, CustomState> | null {
  return isInvexaPresetKey(key) ? presetStrategy(key) : null;
}
